/**
 * Plan of Record — Org Synopsis.
 *
 * The audience-shaped read view for the whole organization: what the plan is,
 * what condition it is in, how far along it is, what is committed (and what is
 * blocked, with the blocker named), the primary KPIs, and the latest decisions.
 * One calm column, read-only, printable. "The answer is a URL" — this page is
 * the URL: /planning/plan-of-record/:id/synopsis (Copy link in the header).
 *
 * Deliberately NOT wired into the page-visibility settings system — the
 * synopsis is always available.
 */

import { Target } from 'lucide-react';
import type { AssembledPor, PorCommitment, PorCommitmentStatus } from '../../types/plan-of-record';
import { ConditionBand } from '../../components/plan-of-record/ConditionBand';
import { PorProgressSummary } from '../../components/plan-of-record/PorProgressSummary';
import { PorNarrativeMarkdown } from '../../components/plan-of-record/PorNarrativeMarkdown';
import {
  CommitmentStatusPill,
  CopyLinkButton,
  KpiReadout,
  PorCard,
  PorCardHeader,
  PorEmptyState,
  PorStatusPill,
  formatDate,
} from '../../components/plan-of-record/porUi';
import { EVENT_LABELS, EVENT_ACCENT } from './PorChangeLog';

const GROUP_ORDER: PorCommitmentStatus[] = ['active', 'blocked', 'watch', 'next', 'complete'];

const GROUP_LABELS: Record<PorCommitmentStatus, string> = {
  active: 'Active',
  blocked: 'Blocked',
  watch: 'Watch',
  next: 'Next',
  complete: 'Complete',
};

export default function PorSynopsis({ assembled }: { assembled: AssembledPor }) {
  const { record } = assembled;
  const primaryKpis = assembled.kpis.filter((k) => k.tier === 'primary');
  const recentEvents = assembled.governance_events.slice(0, 5);

  const groups = GROUP_ORDER.map((status) => ({
    status,
    items: assembled.commitments.filter((c) => c.status === status),
  })).filter((g) => g.items.length > 0);

  return (
    <div className="space-y-5 max-w-3xl">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <header className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--text-ter)]">
            Org Synopsis
          </div>
          <h1 className="text-2xl font-heading font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mt-0.5">
            {record.title}
          </h1>
          <div className="flex items-center gap-3 mt-1.5 flex-wrap">
            <span className="text-xs font-mono text-[var(--text-ter)]">v{record.version}</span>
            <PorStatusPill status={record.status} />
            {(record.horizon_short || record.horizon_long) && (
              <span className="text-xs text-[var(--text-ter)]">
                Horizon · {record.horizon_short ?? '—'} / {record.horizon_long ?? '—'}
              </span>
            )}
          </div>
        </div>
        <CopyLinkButton className="flex-shrink-0 mt-1" />
      </header>

      {/* ── Signal condition + rationale ────────────────────────────────── */}
      <PorCard>
        <ConditionBand assembled={assembled} readOnly />
      </PorCard>

      {/* ── Progress rollup ─────────────────────────────────────────────── */}
      <PorProgressSummary assembled={assembled} />

      {/* ── Commitments by status ───────────────────────────────────────── */}
      <PorCard>
        <PorCardHeader title="Committed Initiatives" count={assembled.commitments.length} />
        {groups.length === 0 ? (
          <PorEmptyState
            icon={<Target className="h-6 w-6" />}
            title="No committed initiatives yet"
            prompt="When the deciding team names commitments, they appear here for the whole organization to see."
          />
        ) : (
          <div className="space-y-4">
            {groups.map((group) => (
              <div key={group.status}>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                    {GROUP_LABELS[group.status]}
                  </span>
                  <span className="text-[11px] font-mono text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                    {group.items.length}
                  </span>
                </div>
                <div className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
                  {group.items.map((c) => (
                    <SynopsisCommitmentRow key={c.id} commitment={c} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </PorCard>

      {/* ── Primary KPIs ────────────────────────────────────────────────── */}
      <PorCard>
        <PorCardHeader title="Primary KPIs" count={primaryKpis.length} hint="No data ≠ zero." />
        {primaryKpis.length === 0 ? (
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
            No primary-tier KPIs on this Plan of Record.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {primaryKpis.map((kpi) => (
              <KpiReadout key={kpi.id} kpi={kpi} />
            ))}
          </div>
        )}
      </PorCard>

      {/* ── Recent decisions ────────────────────────────────────────────── */}
      <PorCard>
        <PorCardHeader
          title="Recent Decisions"
          count={recentEvents.length}
          hint="The 5 latest entries from the immutable Change Log."
        />
        {recentEvents.length === 0 ? (
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
            No governance events recorded yet.
          </p>
        ) : (
          <div className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
            {recentEvents.map((ev) => (
              <div key={ev.id} className="py-2.5 flex items-start gap-3">
                <span
                  className={`mt-1.5 h-2 w-2 rounded-full flex-shrink-0 ${EVENT_ACCENT[ev.event_type] ?? 'bg-[var(--rule-bright)]'}`}
                />
                <div className="min-w-0 flex-1">
                  <span className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                    {EVENT_LABELS[ev.event_type] ?? ev.event_type.replace(/_/g, ' ')}
                  </span>
                  {ev.summary && (
                    <span className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]"> — {ev.summary}</span>
                  )}
                  <div className="text-[11px] text-[var(--text-ter)] mt-0.5">
                    {formatDate(ev.created_at)}
                    {ev.actor_email ? ` · ${ev.actor_email}` : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </PorCard>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <footer className="flex flex-wrap items-center gap-x-6 gap-y-1 border-t border-[var(--rule)] dark:border-[var(--rule)] pt-4 text-xs text-[var(--text-ter)]">
        <span>Version {record.version}</span>
        <span>Last updated {formatDate(record.last_updated)}</span>
        <span className="ml-auto font-mono">Read-only view</span>
      </footer>
    </div>
  );
}

function SynopsisCommitmentRow({ commitment: c }: { commitment: PorCommitment }) {
  return (
    <div className="py-2.5">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">{c.title}</span>
        <CommitmentStatusPill status={c.status} />
        {c.target_window_label && (
          <span className="text-[11px] text-[var(--text-ter)]">Window · {c.target_window_label}</span>
        )}
      </div>
      {c.status === 'blocked' && c.blocker_description && (
        <div className="mt-1 text-xs text-luminous-coral-800 dark:text-luminous-coral-300">
          <span className="font-semibold block mb-0.5">Blocked</span>
          <PorNarrativeMarkdown source={c.blocker_description} />
        </div>
      )}
    </div>
  );
}
